import { useContext, useEffect, useState } from "react";
import { X, ChevronDown, ExternalLink } from "lucide-react";
import { isEmpty } from "lodash-es";
import psl from "psl";
import supersub from "remark-supersub";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import rehypeKatex from 'rehype-katex';

import { AppStateContext } from "@/state/AppStateContext";
import { Citation } from "@/api/models";
import { getSasUrl } from "@/api/api";
import { getFileIcon } from '@/utils/fileValidation';
import { citationKey, dedupeCitations } from '@/utils/citations';
import { CodeBlock } from "@/components/Markdown/CodeBlock";
import { MemoizedReactMarkdown } from "@/components/Markdown/MemoizedReactMarkdown";

import globe from '@/assets/globe.svg';
import globe_white from '@/assets/globe_white.svg';

/**
 * Strip common OCR noise so citation text is readable. Keeps the embedded
 * <h1>/<h2> tags — they're the only structure in the OCR text and render as
 * real headings via rehypeRaw, breaking the content into sections.
 */
const cleanContent = (raw: string | null | undefined): string =>
  (raw || '')
    .replace(/:(?:un)?selected:/gi, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const CitationsPanel = () => {
  const appStateContext = useContext(AppStateContext);
  const theme = appStateContext?.state.theme;
  const isOpen = !isEmpty(appStateContext?.state.selectedCitations);

  // De-duplicated list of citations to render.
  const citations: Citation[] = dedupeCitations(appStateContext?.state.selectedCitations);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const closeCitation = () => appStateContext?.dispatch({ type: 'SET_SELECTED_CITATIONS', payload: null });

  // Reset card state when the selection changes; auto-expand when there's a single source.
  useEffect(() => {
    if (citations.length === 1) setExpanded({ [citationKey(citations[0]) || 'idx-0']: true });
    else setExpanded({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appStateContext?.state.selectedCitations]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeCitation(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * Handle formatting how citation is displayed inline in response
   *
   * @param {Citation} citation - The model's response object
   * @param {number} index - The model's response object
  **/
  const createCitationTitle = (citation: Citation, index: number) => {
    let citationText = "";

    // Document citation
    if (citation.title) citationText = citation.title;
    // URL citation
    else if (citation.type && citation.type == 'url_citation') {
      try {
        const host = new URL(citation.url_citation?.url || '').hostname.toLowerCase().replace(/^www\./, '');
        const result = psl.parse(host);

        if ('sld' in result && result.sld) citationText = result.subdomain ? `${result.subdomain}.${result.sld}` : result.sld;
      } catch {
        citationText = `link ${index}`;
      }
    }
    else citationText = `${index}`;

    return citationText;
  }

  /**
   * Handle getting SAS URL for selected file and opening it
   *
   * @param {Citation} citation - The selected citation
  **/
  const onOpenCitation = async (citation: Citation) => {
    // Document citation
    if (citation.title && (isEmpty(appStateContext?.state.currentCustomGPT) || appStateContext?.state.currentCustomGPT.files_downloadable)) {
      // URL
      if (citation.url && !citation.url.startsWith('https://asappsmsstore.blob.core.windows.net/')) window.open(citation.url, "_blank");
      // File
      else {
        // File Conversation
        let file = (appStateContext?.state.currentChat?.id || '') + '/' + (citation.title || '');
        let blob_name = 'fileconv';

        // Custom GPT Conversation
        if (appStateContext?.state.currentCustomGPT && !isEmpty(appStateContext.state.currentCustomGPT.id) && citation?.source == 'customgpt') {
          file = citation.title || '';
          blob_name = appStateContext?.state.currentCustomGPT?.id;
        }
        // Custom GPT Selector
        else if (appStateContext?.state.selectedTool == 'cgpt_selector' && citation?.source == 'customgpt') {
          file = citation.title || '';
          blob_name = citation.url?.substring(citation.url?.indexOf('https://asappsmsstore.blob.core.windows.net/') + 44, 80) || '';
        }

        // Get SAS URL and open file
        const citation_sas_url = await getSasUrl(file, blob_name);
        citation.sas_url = citation_sas_url.blob_url;
        if (citation.sas_url) window.open(citation.sas_url, "_blank");
      }
    }
    // URL citation
    else if (citation.type && citation.type == 'url_citation') window.open(citation.url_citation?.url, '_blank');
  }

  const canOpen = isEmpty(appStateContext?.state.currentCustomGPT) || appStateContext?.state.currentCustomGPT.files_downloadable == 1;

  return (
    <div className={`flex flex-col h-full side-panel-right ${isOpen ? 'open' : ''} p-1 ps-0`}>
      <div className="h-full bg-base-100 rounded-3xl px-5 pb-5 overflow-y-auto" role="region" aria-label="Citations">
        <div className="flex items-center justify-between border-b border-secondary dark:text-white pt-5 pb-2 px-2 sticky top-0 bg-base-100 z-10">
          <h4>Citations{citations.length ? ` (${citations.length})` : ''}</h4>
          <button type="button" aria-label="Close citations" className="cursor-pointer" onClick={closeCitation}>
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-2 mt-2">
          {citations.map((citation, idx) => {
            const key = citationKey(citation) || `idx-${idx}`;
            const isUrl = !!citation.url_citation;
            const title = createCitationTitle(citation, idx);
            const open = !!expanded[key];

            return (
              <div key={key} className="bg-neutral rounded-2xl overflow-hidden">
                {/* Collapsed header row */}
                <div className="flex items-center gap-2 p-3">
                  <button
                    type="button"
                    onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
                    className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  >
                    {isUrl ? (
                      !isEmpty(citation.url_citation?.favicon_url) ? (
                        <img
                          key={`${citation.url_citation?.favicon_url}__${theme}`}
                          src={citation.url_citation?.favicon_url || ''}
                          alt=""
                          className="rounded-full w-5 h-5 m-0 shrink-0"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = (theme == 'dark' ? globe_white : globe);
                          }}
                        />
                      ) : (
                        <img src={theme == 'dark' ? globe_white : globe} alt="" className="w-5 h-5 m-0 shrink-0" />
                      )
                    ) : (
                      <img src={getFileIcon(title || '')} alt="" width="20" className="shrink-0" />
                    )}
                    <span className="font-bold truncate">{title}</span>
                    <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                  </button>

                  {canOpen && (
                    <button
                      type="button"
                      aria-label={`Open ${title}`}
                      className="cursor-pointer shrink-0"
                      onClick={() => onOpenCitation(citation)}
                    >
                      <ExternalLink size={16} />
                    </button>
                  )}
                </div>

                {/* Expanded body */}
                {open && (
                  <div className="px-3 pb-3">
                    {isUrl ? (
                      <div className="max-w-full">
                        {citation.url_citation?.title && <div className="text-sm mb-1">{citation.url_citation.title}</div>}
                        <a className="text-primary text-xs break-all" href={citation.url_citation?.url || ''} target="_blank" rel="noopener noreferrer">
                          {citation.url_citation?.url || ''}
                        </a>
                      </div>
                    ) : (
                      <div className="model-response markdown-body prose dark:prose-invert !text-[11px] leading-relaxed max-w-full bg-base-100 rounded-xl p-3">
                        <MemoizedReactMarkdown
                          remarkPlugins={[remarkGfm, remarkMath, supersub]}
                          rehypePlugins={[rehypeRaw, rehypeSanitize, rehypeKatex]}
                          components={{
                            code({ className, children, ...props }) {
                              const match = /language-(\w+)/.exec(className || '');

                              return match ? (
                                <CodeBlock
                                  key={Math.random()}
                                  language={(match && match[1]) || ''}
                                  value={String(children).replace(/\n$/, '')}
                                  {...props}
                                />
                              ) : (
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              );
                            },
                          }}
                        >
                          {cleanContent(citation.content)}
                        </MemoizedReactMarkdown>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default CitationsPanel;
